// encounter-delta.ts — surgical encounter_update_delta handler.
//
// The encounter-rooted companion to caster-delta.ts (plans/encounter-rooted-ws-deltas.md,
// pivot §4.6). process_affected_ids ships one section delta per encounter per action —
// per-creature changes keyed by ec_id (the SAME section paths the caster channel uses;
// one patch engine, two roots) plus a diff of the encounter's non-creature top level —
// instead of broadcasting the whole multi-MB encounter blob.
//
// We patch the canonical encounter store IN PLACE (encounterCache row objects ARE the
// casterCache aliases and widget.encounterArr — the §4c single-store rule, so object
// identity is preserved through every patch), then delegate the ENTIRE repaint to the
// existing handleEncounterUpdate after patching the canonical cache blob in place:
// a shallow copy of the store whose patched rows are shallow-copied too. Section patches
// assign NEW section objects, so the copies keep every pre-patch section reference —
// handleEncounterUpdate's old-vs-new reads (bounds, light/weather/turn flags, creature +
// inventory membership, persistent-AOE collect) all see true pre-patch state, while the
// per-token visuals it animates are DOM-dataset-driven and need no old blob at all.
//
// Version/gap semantics (owner-locked): `encounter_ver:{id}` stamps every encounter-channel
// message; a gap heals via ONE thin get_encounter_arr.php fetch routed through the full
// handleEncounterUpdate path (which re-aliases casterCache rows itself).

import type { WSMessage } from './types';
import { createStateRecovery } from './state-recovery';
import { traceEncounterTruth } from './encounter-truth-trace';
import type { EncounterArr } from '../shared/array-types';
import { encounterCreatureField, inventoryField } from '../shared/array-types';
import { ftApplyPath, isMutableDeltaRecord, type MutableDeltaRecord } from './caster-delta';
import { handleEncounterUpdate, handleEncounterRowsPatched, claimCatalogHeal, registerEncounterCatalogHeal, viewerPlayerCasterId } from './encounter-update';
import { applyCasterSideEffects, recoverActiveViewerCasterAfterEncounterDeparture } from './caster-update';
import { bankLegacyDeltaTruth, bankLegacyRowTruth, finishTruthCorrection } from './movement-truth';
import type { MovementTruthRow } from '../janus-engine/movement/client-truth-commit';
import { ftCarryPermissionsForward, ftCollectInventoryPermissions, ftApplyInventoryPermissions } from './caster-cache';
import { compileFetchedEntity } from '../shared/compile-fetched-entity';
import { readSavedResponse, fetchSavedResponse } from '../shared/read-saved-response';
import { yieldForCacheWrite } from './cache-write-task';
import { casterScalarPaths } from '../shared/caster-view-changes';
import { ftCurrentTruthSink } from '../shared/truth-sink';
import { applyExplorationStateRows } from '../janus-engine/compile/from-base-encounter-exploration';

type WSEncounterUpdateDeltaMessage = Extract<WSMessage, { type: 'encounter_update_delta' }>;
type WSDeltaChange = { path: string; value?: unknown; removed?: boolean };
interface EncounterDeltaBlob extends MutableDeltaRecord {
    encounter_creatures_arr: MutableDeltaRecord[];
    encounter_inventories_arr?: MutableDeltaRecord[] | null;
    _ws_version?: number;
    viewer_inventory_map_edit?: unknown;
    item_layer_types?: MutableDeltaRecord;
}

function isEncounterDeltaBlob(value: unknown): value is EncounterDeltaBlob {
    return isMutableDeltaRecord(value)
        && Array.isArray(value.encounter_creatures_arr)
        && value.encounter_creatures_arr.every(isMutableDeltaRecord);
}

function isEncounterArr(value: unknown): value is EncounterArr {
    return isMutableDeltaRecord(value) && Array.isArray(value.encounter_creatures_arr);
}

function ecIdOfRow(row: unknown): string {
    const r = isMutableDeltaRecord(row) ? row : null;
    return String(encounterCreatureField(r?.encounter_creatures, 'encounter_creature_id') ?? '');
}

function invIdOfRow(row: unknown): string {
    return String(inventoryField(row, 'inventory_id') ?? '');
}

// In-flight dedup + the heal path: a version gap (or unknown manifest member) means our
// store is behind the server in an unknown way, so fetch and compile the base envelope
// and run it through the FULL handler, which swaps the
// store, re-aliases casterCache keys, and repaints everything. The endpoint stamps
// `_ws_version` into the blob so the next delta gap-checks instead of adopting blind.
const recoverEncounter = createStateRecovery('encounter', async (encounterId, signal) => {
    const response = await fetchSavedResponse(`${BASE_URL}options/tools/encounters/select/get_encounter_arr.php`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        // The heal installs the parent's game-truth cache: keep private inventory (rendering hides it; the WS full push carries it too).
        body: JSON.stringify({ encounter_id: encounterId, game_truth_rows: 1 }),
        signal,
    });
    if (!response.ok) throw new Error(`get_encounter_arr HTTP ${response.status}`);
    const fresh = await compileFetchedEntity(await readSavedResponse(response));
    signal.throwIfAborted();
    // The computed surface is painted. Present it before the canonical store
    // and shared UI reconciliation continue in another task.
    await yieldForCacheWrite();
    signal.throwIfAborted();
    if (!isEncounterArr(fresh)) throw new Error('Invalid encounter recovery payload');
    const correction = finishTruthCorrection(encounterId);
    await handleEncounterUpdate({ type: 'encounter_update', encounter_arr: fresh,
        version: typeof fresh._ws_version === 'number' ? fresh._ws_version : undefined }, correction);
    const held = window.FateTide?.encounterCache?.get(encounterId)?._ws_version;
    const fetched = typeof fresh._ws_version === 'number' ? fresh._ws_version : undefined;
    return typeof held === 'number' ? Math.max(held, fetched ?? held) : fetched;
});
/** Local rooms already hold saved truth. A refused action must not refetch or reinstall their room. */
function resyncEncounter(encounterId: string, version?: number): Promise<void> {
    return ftCurrentTruthSink() ? Promise.resolve() : recoverEncounter(encounterId, version);
}
// A full push that brought a joining creature without its catalog definitions heals through the same full read.
registerEncounterCatalogHeal(encounterId => { void resyncEncounter(encounterId); });

function applyChanges(target: MutableDeltaRecord, changes: unknown): void {
    if (!Array.isArray(changes)) return;
    for (const ch of changes as WSDeltaChange[]) {
        if (!ch || typeof ch.path !== 'string') continue;
        ftApplyPath(target, ch.path, ch.value, ch.removed === true);
    }
}

function validExplorationDelta(value: unknown, encounterId: string): value is NonNullable<WSEncounterUpdateDeltaMessage['exploration']> {
    if (!isMutableDeltaRecord(value)) return false;
    return value.schema === 'ft.ft3d.team-exploration-delta.v1'
        && String(value.encounterId ?? '').toUpperCase() === encounterId.toUpperCase()
        && ['union', 'reset', 'reconcile'].includes(String(value.operation ?? ''))
        && (value.projection === 'authorized-refetch' || (value.projection === 'held-state' && Array.isArray(value.states)));
}

/** Relay raw exploration changes to the scene's held-state consumer. */
function bridgeExplorationDelta(
    encounterId: string,
    exploration: NonNullable<WSEncounterUpdateDeltaMessage['exploration']>,
    version: number | undefined,
    gap: boolean,
): void {
    const held = window.FateTide?.encounterCache?.get(encounterId);
    if (exploration.projection === 'held-state' && isMutableDeltaRecord(held) && exploration.states) {
        applyExplorationStateRows(held, exploration.states);
    }
    const payload = { ...exploration, encounterId, encounterVersion: version ?? null, gap };
    (window as Window & { __ft3dExplorationDelta?: Record<string, unknown> }).__ft3dExplorationDelta = {
        ...payload,
        receivedAt: new Date().toISOString(),
    };
    window.dispatchEvent(new CustomEvent('ft3d-exploration-changed', { detail: payload }));
    document.querySelectorAll<HTMLIFrameElement>('[data-ft3d-encounter-frame]').forEach(frame => {
        try {
            frame.contentWindow?.postMessage({
                type: 'ft3d-campaign-exploration-changed-v1',
                payload,
            }, window.location.origin);
        } catch (e) { /* a detached/reloading frame heals on its initial perception read */ }
    });
}

async function handleEncounterUpdateDelta(parsedData: WSEncounterUpdateDeltaMessage): Promise<void> {
    const finishTrace = traceEncounterTruth('encounter-update-delta', parsedData.encounter_id, { version: parsedData.version });
    try { await applyEncounterUpdateDelta(parsedData); }
    finally { finishTrace?.(); }
}

async function applyEncounterUpdateDelta(parsedData: WSEncounterUpdateDeltaMessage): Promise<void> {
    const encounterId = parsedData.encounter_id;
    if (!encounterId || !checkIfValidUUID(encounterId)) return;

    const encCache = window.FateTide?.encounterCache;
    const cachedBlob = encCache instanceof Map ? encCache.get(encounterId) : undefined;
    const blob = isEncounterDeltaBlob(cachedBlob) ? cachedBlob : undefined;
    const exploration = validExplorationDelta(parsedData.exploration, encounterId)
        ? parsedData.exploration
        : null;
    const incoming = typeof parsedData.version === 'number' ? parsedData.version : undefined;

    // No full canonical blob → nothing to patch. Map pages (intMap) subscribe to every
    // encounter channel but hold no blob — mirror the full handler's pin-roster hook and
    // stop. Deliberately NO resync here: fetching a whole encounter per action for every
    // pin on the map would be the exact fetch storm this wire exists to kill.
    if (!blob) {
        if (exploration) bridgeExplorationDelta(encounterId, exploration, incoming, false);
        if (
            typeof window.refreshMapMarkerRostersForEncounters === 'function'
            && document.querySelector(`.leaflet-marker-icon[data-encounter_id="${encounterId}"]`)
        ) {
            try { window.refreshMapMarkerRostersForEncounters([encounterId]); } catch (e) { /* non-fatal */ }
        }
        return;
    }

    const ownActiveIdBeforePatch = String(window.FateTide?.activeViewerCasterId || '').trim();
    const viewerCasterIdBeforePatch = viewerPlayerCasterId(
        blob as unknown as EncounterArr,
        ownActiveIdBeforePatch,
    );
    const viewerCasterWasPresent = !!viewerCasterIdBeforePatch;

    // Gap detection — same contract as caster-delta.ts: deltas apply in strict succession;
    // a numeric gap forces the full re-fetch heal; a missing baseline (legacy/unstamped
    // seed) adopts this delta optimistically.
    const baseline = typeof blob._ws_version === 'number' ? (blob._ws_version as number) : undefined;
    if (incoming !== undefined && baseline !== undefined && incoming <= baseline) return;
    if (incoming !== undefined && baseline !== undefined && incoming !== baseline + 1) {
        ftDebugLog('xz encounter', { deltaGap: encounterId, baseline, incoming });
        if (exploration) bridgeExplorationDelta(encounterId, exploration, incoming, true);
        void resyncEncounter(encounterId, incoming);
        return;
    }

    const explorationOnly = exploration !== null
        && Object.keys(parsedData.creatures ?? {}).length === 0
        && (!Array.isArray(parsedData.encounter) || parsedData.encounter.length === 0)
        && parsedData.ec_manifest == null
        && Object.keys(parsedData.inventories ?? {}).length === 0
        && parsedData.inv_manifest == null;
    if (exploration) bridgeExplorationDelta(encounterId, exploration, incoming, false);
    if (explorationOnly) {
        if (incoming !== undefined) blob._ws_version = incoming;
        ftDebugLog('xz encounter', { explorationDeltaApplied: encounterId, version: incoming });
        return;
    }

    const rows = blob.encounter_creatures_arr;
    const creatureEntries = Object.entries(
        (parsedData.creatures ?? {}) as Record<string, { changes?: WSDeltaChange[]; row?: Record<string, unknown> }>,
    );

    // Truth-model 'scope' wire: UUID-keyed loose-inventory row adopts + membership manifest.
    // Absent fields on older/shadow publishes → hasTypedInventories false → every
    // typed inventory block below no-ops.
    const inventoryEntries = Object.entries(
        (parsedData.inventories ?? {}) as Record<string, { row?: Record<string, unknown> | null }>,
    );
    const patchedInvIds = new Set(inventoryEntries.map(([id]) => id));
    const hasTypedInventories = inventoryEntries.length > 0 || Array.isArray(parsedData.inv_manifest);

    // Pre-validate inventory membership BEFORE any mutation (scope-review #7): every
    // manifest member must be a row we already hold or one this message adopts whole —
    // otherwise our store is behind in an unknown way, and healing from a still-pristine
    // blob is strictly safer than healing after a partial apply.
    // A null floor is awaiting the inventory compiler, not missing individual members.
    if (blob.encounter_inventories_arr !== null && Array.isArray(parsedData.inv_manifest)) {
        const heldInvIds = new Set(
            (Array.isArray(blob.encounter_inventories_arr) ? blob.encounter_inventories_arr : []).map(invIdOfRow),
        );
        for (const id of parsedData.inv_manifest) {
            if (!heldInvIds.has(id) && !patchedInvIds.has(id)) {
                ftDebugLog('xz encounter', { deltaInvMembershipStale: encounterId, missing: id });
                void resyncEncounter(encounterId, incoming);
                return;
            }
        }
    }

    // Dual-publish collision guard (scope-review #3): while FT_TRUTH_INV_SHELL_DUAL keeps
    // the legacy whole-array inventory change in the shell diff for pre-deploy tabs, a
    // client that understands the typed ops must IGNORE that legacy path — applying it
    // first would replace the held row objects (losing identity + viewer permissions)
    // right before the "in-place" adopts run against the replacements.
    const encounterChanges = hasTypedInventories && Array.isArray(parsedData.encounter)
        ? parsedData.encounter.filter(
            (ch) => !ch || typeof ch.path !== 'string' || ch.path !== 'encounter_inventories_arr',
        )
        : parsedData.encounter;

    // The viewer-scoped floor-drag stamp only exists client-side (get_encounter_arr.php
    // stamps it per viewer; the PAI wire never carries it) — capture it before a patched
    // item_layer_types section can drop it. Mirrors the full handler's vim preserve.
    const prevVimTop = blob.viewer_inventory_map_edit;
    const prevVimIlt = blob.item_layer_types?.viewer_inventory_map_edit;

    // 1. Encounter-level sections (shell diff vs the server's enc_shell sidecar) — with
    // the legacy inventory-array path filtered out when typed inventory ops are present.
    applyChanges(blob, encounterChanges);
    if (prevVimTop != null && blob.viewer_inventory_map_edit == null) {
        blob.viewer_inventory_map_edit = prevVimTop;
    }
    if (
        prevVimIlt != null
        && blob.item_layer_types && typeof blob.item_layer_types === 'object'
        && blob.item_layer_types.viewer_inventory_map_edit == null
    ) {
        blob.item_layer_types.viewer_inventory_map_edit = prevVimIlt;
    }

    // 2. Per-creature sections into the canonical rows (identity preserved). A `row` entry
    // is a whole finalized blob (no server snapshot / over threshold / spawn): adopt it into
    // the existing row in place — the adoptCasterBlobIntoEncounterRow pattern — or append it
    // as the new-creature path. Carry the caster-channel `_ws_version` across an adopt: the
    // two channels keep independent chains (row-level vs blob-level).
    const rowsById = new Map<string, MutableDeltaRecord>(rows.map((r) => [ecIdOfRow(r), r]));
    let membershipStale = false;
    // The one-row echo census (2026-09-10): a delta that only patches creatures the store already
    // holds — no shell change, no inventory adopt, no roster add/prune — takes the narrow
    // handleEncounterRowsPatched pass instead of the whole-encounter one (see encounter-update.ts).
    let rosterChanged = false;
    const patchedCreatureIds: string[] = [];
    for (const [ecId, entry] of creatureEntries) {
        if (!entry || typeof entry !== 'object') continue;
        const row = rowsById.get(ecId);
        if (entry.row && typeof entry.row === 'object') {
            if (row) {
                const casterVersion = typeof row._ws_version === 'number' ? row._ws_version : undefined;
                // Carry the viewer's seed-correct permissions across the in-place whole-row swap —
                // the broadcast row is stripped (viewer-agnostic wire). [unified-viewer-scoping Chunk 2]
                // Snapshot nested inventory perms before the clobber, re-apply after — the whole-row
                // finalized carries the creature's inventory and it is stripped too. [Chunk 3]
                const prevPermissions = row.permissions;
                const prevInvPerms = ftCollectInventoryPermissions(row);
                for (const k of Object.keys(row)) delete row[k];
                Object.assign(row, entry.row);
                ftCarryPermissionsForward(row, prevPermissions);
                ftApplyInventoryPermissions(row, prevInvPerms);
                if (casterVersion !== undefined && typeof row._ws_version !== 'number') {
                    row._ws_version = casterVersion;
                }
            } else {
                rows.push(entry.row);
                rowsById.set(ecId, entry.row);
                rosterChanged = true;
                // A delta ships no catalog: a joining row whose definitions this page lacks heals via the full read below.
                if (claimCatalogHeal(encounterId, entry.row, blob.catalog)) membershipStale = true;
            }
        } else if (Array.isArray(entry.changes)) {
            if (!row) {
                // Section changes for a creature we don't hold — membership is stale.
                membershipStale = true;
                continue;
            }
            applyChanges(row, entry.changes);
        }
        // R1 (2026-09-07): a legacy delta that installed or changed this row is saved truth with provenance for
        // the client-truth adapters — banked explicitly from the INSTALLED PAYLOAD (the whole row, or the rebuilt
        // sections a section delta carried), never from the merged rendered row, whose untouched fields may be
        // this browser's own optimism or a peer's provisional (Codex M1c review, finding 1).
        const installed = rowsById.get(ecId);
        if (!installed) continue;
        void window.ftAdoptInventoryBase?.(installed);
        patchedCreatureIds.push(ecId);
        if (entry.row && typeof entry.row === 'object') {
            bankLegacyRowTruth(encounterId, ecId, installed as unknown as MovementTruthRow, 'legacy-delta');
        } else if (Array.isArray(entry.changes)) {
            const section = (path: string): Record<string, unknown> | null => {
                const change = (entry.changes as WSDeltaChange[]).find(candidate => candidate?.path === path && candidate.removed !== true);
                return change && change.value && typeof change.value === 'object' ? change.value as Record<string, unknown> : null;
            };
            bankLegacyDeltaTruth(encounterId, ecId, installed as unknown as MovementTruthRow, { encounter_creatures: section('encounter_creatures'), position: section('position') }, 'legacy-delta');
        }
    }

    // 2b. Loose-inventory row adopts (scope wire) — whole finalized rows only, matched by
    // inventory_id, adopted IN PLACE so any held reference stays valid. The broadcast row
    // is permission-stripped (viewer-agnostic wire), so carry the viewer's seed-correct
    // permissions across the clobber — the row's own plus any nested container contents —
    // exactly the creature-adopt pattern above. Ops apply BEFORE manifest validation
    // (a freshly added manifest member is not "unknown" once its row is installed).
    if (hasTypedInventories && blob.encounter_inventories_arr === undefined) {
        blob.encounter_inventories_arr = [];
    }
    const invRows = blob.encounter_inventories_arr;
    const invRowsById = new Map<string, MutableDeltaRecord>(
        Array.isArray(invRows) ? invRows.map((r) => [invIdOfRow(r), r]) : [],
    );
    if (Array.isArray(invRows)) {
        for (const [invId, entry] of inventoryEntries) {
            const incomingRow = entry && typeof entry === 'object' ? entry.row : null;
            if (!incomingRow || typeof incomingRow !== 'object') continue;
            const row = invRowsById.get(invId);
            if (row) {
                const prevPermissions = row.permissions;
                const prevNestedPerms = ftCollectInventoryPermissions(row);
                for (const k of Object.keys(row)) delete row[k];
                Object.assign(row, incomingRow);
                ftCarryPermissionsForward(row, prevPermissions);
                ftApplyInventoryPermissions(row, prevNestedPerms);
            } else {
                invRows.push(incomingRow as MutableDeltaRecord);
                invRowsById.set(invId, incomingRow as MutableDeltaRecord);
            }
        }
    }

    // 3. Membership prune — rows absent from the rebuilt encounter's manifest left the
    // encounter (moved/removed); handleEncounterUpdate fades their tokens out via the
    // A manifest member we neither hold nor received whole means
    // we missed a spawn → heal.
    if (Array.isArray(parsedData.ec_manifest)) {
        const keep = new Set(parsedData.ec_manifest);
        for (let i = rows.length - 1; i >= 0; i--) {
            const id = ecIdOfRow(rows[i]);
            if (id && !keep.has(id)) { rows.splice(i, 1); rosterChanged = true; }
        }
        for (const id of keep) {
            if (!rowsById.has(id)) membershipStale = true;
        }
    }

    // 3b. Inventory membership prune (scope wire) — same contract: prune what the
    // manifest no longer lists from the live array,
    // heal on a manifest member we neither hold nor just received whole.
    if (Array.isArray(parsedData.inv_manifest) && Array.isArray(invRows)) {
        const keepInv = new Set(parsedData.inv_manifest);
        for (let i = invRows.length - 1; i >= 0; i--) {
            const id = invIdOfRow(invRows[i]);
            if (id && !keepInv.has(id)) {
                invRows.splice(i, 1);
                invRowsById.delete(id);
                rosterChanged = true;
            }
        }
        for (const id of keepInv) {
            if (!invRowsById.has(id)) membershipStale = true;
        }
    }

    if (membershipStale) {
        ftDebugLog('xz encounter', { deltaMembershipStale: encounterId });
        void resyncEncounter(encounterId, incoming);
        return;
    }

    if (incoming !== undefined) blob._ws_version = incoming;

    // 4. Pane repaints for patched creatures. Every encounter-owned pane mounts under
    // encounter_creature_id — cheap no-ops when off-screen.
    //
    // The viewer's OWN active caster no longer has its own caster:{id} channel once it lives
    // in a loaded encounter (channels.ts encounter-managed gate, unified-viewer-scoping §4.1
    // chunk 1). That channel used to run the FULL applyCasterSideEffects — the chrome reconcile
    // (reapplyVisibility / syncTurnControls / encounter-switch detection) and the
    // refreshCasterSheet fallback for un-ported PHP tabs — which the cache-only repaints below
    // do NOT cover. So run full side-effects HERE for the own active caster, or that reconcile
    // is lost now the channel is cut. Every OTHER patched creature keeps the cheap cache-only
    // repaints; running full applyCasterSideEffects for all of them would be the per-push PHP
    // re-render storm the open-side-info registry was built to avoid.
    const ownActiveId = window.FateTide?.activeViewerCasterId || '';
    const ownActiveUpper = ownActiveId.toUpperCase();
    for (const [ecId, entry] of creatureEntries) {
        const row = rowsById.get(ecId);
        const changedPaths = entry && !entry.row && Array.isArray(entry.changes) ? entry.changes.map(change => change.path) : undefined;
        const isOwnActive = !!ownActiveUpper
            && ecId.toUpperCase() === ownActiveUpper;

        // Live-stock glue for an open trade/trainer panel, fired for EVERY patched creature —
        // deliberately ABOVE the isOwnActive branch. This is the path a merchant purchase actually
        // takes: accept_trade_inc.php drops encounter/location entries from $affected_ids (an OOM
        // guard) so the trade fans out as per-creature deltas, and the MERCHANT arrives here as a
        // non-own-active row that only gets the cheap cache-only repaints below. Hooking this into
        // applyCasterSideEffects instead would miss it entirely, which is exactly why the panel
        // went stale when another player bought something. No-op when no panel is open.
        // A section-changes entry carries paths, so we can tell the trade side whether this
        // change could possibly matter (inventory / encounter_creatures) and spare it a full
        // fragment recompile on a combat HP tick. A whole-row entry carries no paths — pass
        // undefined so the trade side conservatively refreshes.
        try {
            const changes = entry && !entry.row && Array.isArray(entry.changes) ? entry.changes : null;
            const relevant = changes
                ? changes.some(
                    (ch) => ch && typeof ch.path === 'string' && /^(inventory|encounter_creatures)\b/.test(ch.path),
                )
                : undefined;
            (window as unknown as { ftExchangeOnParticipantUpdate?: (id: string, relevant?: boolean) => void })
                .ftExchangeOnParticipantUpdate?.(ecId, relevant);
        } catch (e) { /* non-fatal */ }

        if (isOwnActive && row) {
            try { applyCasterSideEffects(row, ownActiveId, changedPaths); } catch (e) { /* non-fatal */ }
            // The active viewer's OWN row just changed on the map — most often a MOVE. A container the
            // user has open in the side-info drawer is reach-gated (ftActiveViewerCanReach); walking
            // toward / away from it changes whether its contents are "blocked out". That container's id
            // isn't in this delta's affected set, so nudge the open card to re-render UNCONDITIONALLY
            // (cache-only, self-scoped to whatever's open) so the block updates live as reach changes.
            try { window.ftRerenderOpenSideInfo?.(); } catch (e) { /* non-fatal */ }
            continue;
        }
        if (!checkIfValidUUID(ecId)) continue;
        try { window.ftRerenderOpenInfoPanel?.(ecId); } catch (e) { /* non-fatal */ }
        try { window.ftRerenderPcSheet?.(ecId, changedPaths); } catch (e) { /* non-fatal */ }
        // Abilities-tab class-mechanic charge bars live in the tab shell, not a .ft-pc-sheet-pane,
        // so ftRerenderPcSheet above never touches them — repaint an inspected (non-own-active)
        // creature's bar here too, or it goes stale on this delta path. No-op when no host on screen.
        if (!changedPaths || !casterScalarPaths(changedPaths)) try { window.ftRerenderClassMechanicBars?.(ecId); } catch (e) { /* non-fatal */ }
    }

    // 5. Rows were patched in place. A creature-only delta (the move echo: two creatures' position +
    // encounter_creatures sections, nothing else) takes the one-row pass — no whole-roster re-intern,
    // re-bank or blob walk, and the FT3D parent projects actors only. Anything that touched the shell,
    // the loose inventory or the roster runs the canonical whole-encounter adoption path.
    const creatureRowsOnly = patchedCreatureIds.length > 0
        && !rosterChanged
        && inventoryEntries.length === 0
        && !(Array.isArray(encounterChanges) && encounterChanges.length > 0);
    if (creatureRowsOnly) {
        handleEncounterRowsPatched(blob as unknown as EncounterArr, patchedCreatureIds);
    } else {
        await handleEncounterUpdate({ type: 'encounter_update', encounter_arr: blob as EncounterArr });
    }

    if (
        !window.FateTide?.user?.isGM
        && viewerCasterWasPresent
        && !rows.some((row) => ecIdOfRow(row).toUpperCase() === viewerCasterIdBeforePatch.toUpperCase())
    ) {
        await recoverActiveViewerCasterAfterEncounterDeparture(encounterId, viewerCasterIdBeforePatch);
    }

    ftDebugLog('xz encounter', {
        deltaApplied: encounterId,
        creatures: creatureEntries.length,
        inventories: inventoryEntries.length,
        encounterChanges: Array.isArray(parsedData.encounter) ? parsedData.encounter.length : 0,
        version: incoming,
    });
}

window.handleEncounterUpdateDelta = handleEncounterUpdateDelta;
window.ftResyncEncounter = resyncEncounter;

export { handleEncounterUpdateDelta, resyncEncounter };
